import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next/src';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import Switch from '@hobs/components/Switch';
import InputLabel from '@hobs/components/InputLabel';
import Dialog from '@hobs/components/Dialog';
import Toast from '@hobs/components/Toast';
import { Formik, Form, Field } from 'formik';
import Input from '@hobs/components/Input';
import { RaisedButton } from '@hobs/components/Buttons';
import Alert from '@hobs/components/Alert';

import {
  updateActivity,
  clearActivity,
  getActivity
} from '@hobs/actions/activities';
import {
  createActivity,
} from '../../../actions/activities';

import './ActivityDialog.scss';

const defaultState = {
  error: null
};

class ActivityDialog extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = defaultState;

    const { t } = props;
    this.activitySchema = Yup.object().shape({
      name: Yup.string()
        .max(50, t('Name cannot be longer than 50 symbols'))
        .trim('Incorrect input').required(t('Name is required')),
      description: Yup.string()
        .max(256, t('Description cannot be longer than 256 symbols'))
        .nullable()
    });
  }

  isDialogOpen() {
    return this.props.match.params.action === 'activity';
  }

  getSorPathName() {
    const deleteIndex = this.props.location.pathname.indexOf('/activity');
    return this.props.location.pathname.substring(0, deleteIndex);
  }

  createActivity = (values, { setErrors, setSubmitting }) => {
    const { t } = this.props;

    const body = {
      ...values,
    };

    this.props.createActivity(body).then(response => {
      if (response.success) {
        this.onDialogClose();

        Toast.success(t('Activity created successfully'));
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [error.target]: error.message[0]
          };
        }, {});

        setErrors(errors);
      } else if (response.payload) {
        this.setError(response.payload.message);
      }

      setSubmitting(false);
    });
  };

  updateActivity = (values, { setErrors, setSubmitting }) => {
    const { t } = this.props;

    const body = {
      ...values
    };

    this.props.updateActivity(values.activityId, body).then(response => {
      if (response.success) {
        this.onDialogClose();
        Toast.success(t('Activity updated successfully'));
        if(response.payload.isAssigned){
          Toast.info(t('Please note that this activity has already been selected for a service request. The new activity name will not apply to already existing service requests'));
        }
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [error.target]: error.message[0]
          };
        }, {});

        setErrors(errors);
      } else if (response.payload) {
        this.setError(response.payload.message);
      }

      setSubmitting(false);
    });
  };

  onDialogClose = () => {
    const { reloadActivities, history } = this.props;

    history.push(this.getSorPathName());
    if (reloadActivities) {
      reloadActivities();
    }
  };

  onDialogClosed = () => {
    const { clearActivity } = this.props;

    clearActivity();
    this.resetState();
  };

  onDialogOpening = async () => {
    const activityId = this.props.match.params.guid;
    if (activityId) {
      this.props.getActivity(activityId);
    }
  };

  setError(message) {
    this.setState({ error: message });
  }

  resetState() {
    this.setState(defaultState);
  }

  isEdit() {
    const { match, activity } = this.props;
    return match.params.guid || activity;
  }

  getInitialActivityValues() {
    const { activity, isFirstActivity } = this.props;
    if (activity) {
      return {
        activityId: activity.activityId,
        name: activity.name,
        description: activity.description,
        isDefault: activity.isDefault
      };
    } else {
      if(isFirstActivity){
        return {
          name: '',
          description: '',
          isDefault: true
        };
      }
      return {
        name: '',
        description: '',
        isDefault: false
      };
    }
  }

  render() {
    const { t, activity } = this.props;
    const { error } = this.state;
    return (
      <Dialog
        title={t(this.isEdit() ? 'Edit activity' : 'New activity')}
        isOpen={this.isDialogOpen()}
        onClose={this.onDialogClose}
        onClosed={this.onDialogClosed}
        onOpening={this.onDialogOpening}
        className="activity"
      >
        <Formik
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={this.activitySchema}
          enableReinitialize={true}
          initialValues={this.getInitialActivityValues()}
          onSubmit={activity ? this.updateActivity : this.createActivity}
        >
          {({ setFieldValue, isSubmitting, values }) => (
            <Form>
              {error && <Alert type="error">{error}</Alert>}
              <div className="activity--dialog">
                <Field
                  name="name"
                  setFieldValue={setFieldValue}
                  component={Input}
                >
                  {t('Name')}
                </Field>
                <Field
                  name="description"
                  setFieldValue={setFieldValue}
                  component={Input}
                >
                  {t('Description')}
                </Field>
                <div className="default-activity">
                  <InputLabel>{t('Default Activity')}:</InputLabel>
                  <Field
                    name="isDefault"
                    component={Switch}
                    setFieldValue={setFieldValue}
                    checked={values.isDefault}
                    label={values.isDefault ? t('Yes') : t('No')}
                  />
                </div>
                <div className="activity--dialog--button">
                  <RaisedButton type="submit" disabled={isSubmitting}>
                    {t('Save')}
                  </RaisedButton>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  }
}

ActivityDialog.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  reloadActivities: PropTypes.func,
  createActivity: PropTypes.func,
  getActivity: PropTypes.func,
  updateActivity: PropTypes.func,
  deleteActivitynex: PropTypes.func,
  clearActivity: PropTypes.func,
  activity: PropTypes.object,
  isFirstActivity: PropTypes.bool,
};

const mapStateToProps = state => 
  ({
    activity: state.activity.activity
  });

const mapDispatchToProps = {
  createActivity,
  getActivity,
  updateActivity,
  clearActivity
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(ActivityDialog))
);
